import { Html5Qrcode } from 'html5-qrcode';
import {
  AlertTriangle,
  Camera,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Keyboard,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================================================
// TIPOS Y ESTADOS
// ============================================================================

type ScannerState =
  | 'idle' // Esperando que el usuario active la cámara
  | 'requesting' // Solicitando permisos
  | 'scanning' // Cámara activa, escaneando
  | 'error'; // Error (con subtipo)

type ErrorType =
  | 'not_allowed' // Usuario negó permisos
  | 'not_found' // No hay cámara
  | 'not_readable' // Cámara ocupada
  | 'not_secure' // No HTTPS
  | 'in_iframe' // Bloqueado por iframe
  | 'overconstrained' // Restricciones inválidas
  | 'unknown'; // Error desconocido

interface CameraError {
  type: ErrorType;
  message: string;
  technicalDetails?: string;
}

interface QRScannerProps {
  onScan: (ticketCode: string) => void;
  isScanning: boolean;
}

// ============================================================================
// UTILIDADES DE DIAGNÓSTICO
// ============================================================================

function diagnoseEnvironment(): { isSecure: boolean; isIframe: boolean; protocol: string } {
  const isSecure = window.isSecureContext ?? false;
  const isIframe = window.self !== window.top;
  const protocol = window.location.protocol;

  console.log('📋 [QRScanner] Diagnóstico del entorno:', {
    isSecureContext: isSecure,
    protocol,
    isIframe,
    userAgent: navigator.userAgent,
    hasMediaDevices: !!navigator.mediaDevices,
    hasGetUserMedia: !!navigator.mediaDevices?.getUserMedia,
  });

  return { isSecure, isIframe, protocol };
}

async function checkCameraPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    if (navigator.permissions?.query) {
      const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
      console.log('🔐 [QRScanner] Estado de permiso:', result.state);
      return result.state as 'granted' | 'denied' | 'prompt';
    }
  } catch (e) {
    console.log('🔐 [QRScanner] No se pudo consultar permisos:', e);
  }
  return 'unknown';
}

function classifyError(error: unknown): CameraError {
  const err = error as Error;
  const name = err?.name || '';
  const message = err?.message || String(error);

  console.error('❌ [QRScanner] Error capturado:', { name, message, stack: err?.stack });

  // Verificar contexto seguro primero
  if (!window.isSecureContext) {
    return {
      type: 'not_secure',
      message: 'La cámara requiere una conexión segura (HTTPS)',
      technicalDetails: `Protocolo actual: ${window.location.protocol}. Para usar la cámara en red local, accede desde localhost o configura HTTPS.`,
    };
  }

  // Verificar iframe
  if (
    window.self !== window.top &&
    (name === 'NotAllowedError' ||
      message.includes('NotAllowedError') ||
      message.includes('denied'))
  ) {
    return {
      type: 'in_iframe',
      message: 'La cámara está bloqueada porque la página está dentro de un marco',
      technicalDetails: 'El iframe no tiene el atributo allow="camera"',
    };
  }

  // Detectar NotAllowedError tanto por name como por mensaje (html5-qrcode lo envuelve)
  if (name === 'NotAllowedError' || message.includes('NotAllowedError')) {
    return {
      type: 'not_allowed',
      message: 'Permiso de cámara denegado',
      technicalDetails: message,
    };
  }

  // Detectar NotFoundError
  if (name === 'NotFoundError' || message.includes('NotFoundError')) {
    return {
      type: 'not_found',
      message: 'No se detectó ninguna cámara en este dispositivo',
      technicalDetails: message,
    };
  }

  // Detectar NotReadableError
  if (name === 'NotReadableError' || message.includes('NotReadableError')) {
    return {
      type: 'not_readable',
      message: 'La cámara está siendo usada por otra aplicación',
      technicalDetails: message,
    };
  }

  // Detectar OverconstrainedError
  if (name === 'OverconstrainedError' || message.includes('OverconstrainedError')) {
    return {
      type: 'overconstrained',
      message: 'La cámara no soporta la configuración solicitada',
      technicalDetails: message,
    };
  }

  return {
    type: 'unknown',
    message: 'Error al acceder a la cámara',
    technicalDetails: `${name}: ${message}`,
  };
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function QRScanner({ onScan, isScanning }: QRScannerProps) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const lastScannedRef = useRef<string>('');
  const [state, setState] = useState<ScannerState>('idle');
  const [error, setError] = useState<CameraError | null>(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (scannerRef.current?.isScanning) {
        scannerRef.current.stop().catch(console.error);
      }
    };
  }, []);

  // Callback de escaneo exitoso
  const handleScanSuccess = useCallback(
    (decodedText: string) => {
      // Evitar escaneos duplicados
      if (decodedText === lastScannedRef.current || isScanning) return;

      lastScannedRef.current = decodedText;
      console.log('🎫 [QRScanner] Código escaneado:', decodedText);
      onScan(decodedText);

      // Reset después de 3 segundos para permitir nuevo escaneo
      setTimeout(() => {
        lastScannedRef.current = '';
      }, 3000);
    },
    [onScan, isScanning]
  );

  // Iniciar cámara (llamado desde click del usuario)
  const startCamera = useCallback(async () => {
    setState('requesting');
    setError(null);

    // Diagnóstico inicial
    const env = diagnoseEnvironment();

    // Verificar contexto seguro
    if (!env.isSecure) {
      setError({
        type: 'not_secure',
        message: 'La cámara requiere una conexión segura (HTTPS)',
        technicalDetails: `Protocolo actual: ${env.protocol}`,
      });
      setState('error');
      return;
    }

    // Verificar permisos previos
    const permissionState = await checkCameraPermission();
    console.log('🔐 [QRScanner] Permiso previo:', permissionState);

    // Esperar un frame para que el DOM se actualice y el div sea visible
    await new Promise((resolve) => setTimeout(resolve, 100));

    try {
      // Limpiar instancia anterior si existe
      if (scannerRef.current) {
        try {
          if (scannerRef.current.isScanning) {
            await scannerRef.current.stop();
          }
          await scannerRef.current.clear();
        } catch (e) {
          console.log('⚠️ [QRScanner] Error limpiando scanner anterior:', e);
        }
        scannerRef.current = null;
      }

      // Crear nueva instancia del escáner
      scannerRef.current = new Html5Qrcode('qr-scanner-video');

      // Configuración de cámara - preferir trasera en móvil
      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1,
      };

      // Intentar con cámara trasera primero
      try {
        await scannerRef.current.start(
          { facingMode: 'environment' },
          config,
          handleScanSuccess,
          () => {} // Silenciar errores de lectura normales
        );
        setState('scanning');
        console.log('✅ [QRScanner] Cámara trasera iniciada');
        return;
      } catch (backCameraError) {
        console.log('⚠️ [QRScanner] Cámara trasera no disponible, intentando cualquier cámara...');
      }

      // Fallback: cualquier cámara disponible
      await scannerRef.current.start({ facingMode: 'user' }, config, handleScanSuccess, () => {});
      setState('scanning');
      console.log('✅ [QRScanner] Cámara frontal iniciada');
    } catch (err) {
      const classifiedError = classifyError(err);
      setError(classifiedError);
      setState('error');
    }
  }, [handleScanSuccess]);

  // Detener cámara
  const stopCamera = useCallback(async () => {
    if (scannerRef.current?.isScanning) {
      await scannerRef.current.stop();
    }
    setState('idle');
    setError(null);
  }, []);

  // Enviar código manual
  const handleManualSubmit = useCallback(() => {
    const code = manualCode.trim();
    if (code && !isScanning) {
      console.log('⌨️ [QRScanner] Código manual:', code);
      onScan(code);
      setManualCode('');
    }
  }, [manualCode, isScanning, onScan]);

  // Abrir en nueva pestaña (para casos de iframe)
  const openInNewTab = useCallback(() => {
    window.open(window.location.href, '_blank');
  }, []);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="qr-scanner-container space-y-4">
      {/* Instrucciones */}
      <p className="text-sm text-slate-600 text-center">
        Coloca el código QR del ticket dentro del recuadro. Usa la cámara trasera para mejor
        lectura.
      </p>

      {/* Contenedor del video - Visible cuando requesting o scanning */}
      <div
        className="space-y-4"
        style={{
          display: state === 'scanning' || state === 'requesting' ? 'block' : 'none',
        }}
      >
        <div className="relative">
          <div
            id="qr-scanner-video"
            className="rounded-xl overflow-hidden shadow-lg bg-black max-w-md mx-auto"
            style={{ minHeight: '300px', width: '100%' }}
          />
          {/* Overlay de estado */}
          {state === 'scanning' && (
            <div className="absolute top-3 left-3 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1.5">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Escaneando
            </div>
          )}
          {state === 'requesting' && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <div className="text-center text-white">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
                <p className="text-sm">Iniciando cámara...</p>
              </div>
            </div>
          )}
        </div>

        {state === 'scanning' && (
          <button
            onClick={stopCamera}
            className="w-full py-3 border border-slate-300 text-slate-700 font-medium rounded-lg
                       hover:bg-slate-50 transition-colors flex items-center justify-center gap-2"
          >
            <XCircle className="w-4 h-4" />
            Detener cámara
          </button>
        )}
      </div>

      {/* Estado: Idle - Botón para activar cámara */}
      {state === 'idle' && (
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center">
            <Camera className="w-10 h-10 text-slate-400" />
          </div>
          <button
            onClick={startCamera}
            className="w-full sm:w-auto min-h-[56px] px-8 py-4 bg-indigo-600 hover:bg-indigo-700 
                       text-white font-semibold rounded-xl shadow-lg 
                       transition-all duration-200 active:scale-[0.98]
                       flex items-center justify-center gap-3"
          >
            <Camera className="w-5 h-5" />
            Activar cámara y escanear
          </button>
        </div>
      )}

      {/* Estado: Error */}
      {state === 'error' && error && (
        <div className="space-y-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-red-800">{error.message}</h4>

                {/* Instrucciones específicas según el tipo de error */}
                <div className="mt-3 text-sm text-red-700">
                  {error.type === 'not_allowed' && (
                    <div className="space-y-2">
                      <p className="font-medium">Para habilitar la cámara:</p>
                      <ol className="list-decimal list-inside space-y-1 text-red-600">
                        <li>Toca el icono de candado en la barra de direcciones</li>
                        <li>Busca "Cámara" o "Permisos"</li>
                        <li>Cambia a "Permitir"</li>
                        <li>Recarga la página</li>
                      </ol>
                    </div>
                  )}
                  {error.type === 'not_found' && (
                    <p>
                      Verifica que tu dispositivo tenga una cámara funcional y que no esté
                      deshabilitada.
                    </p>
                  )}
                  {error.type === 'not_readable' && (
                    <p>
                      Cierra otras aplicaciones que puedan estar usando la cámara (videollamadas,
                      otras pestañas, etc.)
                    </p>
                  )}
                  {error.type === 'not_secure' && (
                    <p>Esta función solo está disponible en conexiones HTTPS seguras.</p>
                  )}
                  {error.type === 'in_iframe' && (
                    <p>La página está embebida en un marco que bloquea el acceso a la cámara.</p>
                  )}
                </div>

                {/* Detalles técnicos expandibles */}
                {error.technicalDetails && (
                  <button
                    onClick={() => setShowTechnicalDetails(!showTechnicalDetails)}
                    className="mt-3 text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                  >
                    {showTechnicalDetails ? (
                      <ChevronUp className="w-3 h-3" />
                    ) : (
                      <ChevronDown className="w-3 h-3" />
                    )}
                    {showTechnicalDetails ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
                  </button>
                )}
                {showTechnicalDetails && error.technicalDetails && (
                  <pre className="mt-2 text-xs bg-red-100 p-2 rounded overflow-x-auto text-red-800">
                    {error.technicalDetails}
                  </pre>
                )}
              </div>
            </div>
          </div>

          {/* Botones de acción según el error */}
          <div className="flex flex-col sm:flex-row gap-3">
            {error.type !== 'not_secure' && error.type !== 'in_iframe' && (
              <button
                onClick={startCamera}
                className="flex-1 min-h-[48px] px-6 py-3 bg-indigo-600 hover:bg-indigo-700 
                           text-white font-semibold rounded-xl transition-colors
                           flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            )}
            {error.type === 'in_iframe' && (
              <button
                onClick={openInNewTab}
                className="flex-1 min-h-[48px] px-6 py-3 bg-indigo-600 hover:bg-indigo-700 
                           text-white font-semibold rounded-xl transition-colors
                           flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir en nueva pestaña
              </button>
            )}
          </div>
        </div>
      )}

      {/* Separador */}
      <div className="relative py-2">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-slate-200" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-white px-3 text-sm text-slate-500">o</span>
        </div>
      </div>

      {/* Input manual - siempre visible como fallback */}
      <div className="space-y-3">
        <button
          onClick={() => setShowManualInput(!showManualInput)}
          className="w-full py-2 text-sm text-slate-600 hover:text-slate-800 
                     flex items-center justify-center gap-2 transition-colors"
        >
          <Keyboard className="w-4 h-4" />
          {showManualInput ? 'Ocultar entrada manual' : 'Ingresar código manualmente'}
          {showManualInput ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>

        {showManualInput && (
          <div className="flex gap-2">
            <input
              type="text"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handleManualSubmit()}
              placeholder="Ej: TKT-ABC123"
              className="flex-1 px-4 py-3 border border-slate-300 rounded-lg 
                         focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                         text-center font-mono text-lg uppercase"
              disabled={isScanning}
            />
            <button
              onClick={handleManualSubmit}
              disabled={!manualCode.trim() || isScanning}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300
                         text-white font-semibold rounded-lg transition-colors
                         flex items-center justify-center"
            >
              <CheckCircle className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
